import { getIframeLanguage } from '@/lib/i18n';
import {
  IMPERSONATION_ACCESS_TOKEN_KEY,
  endImpersonationSession,
  isImpersonationTab,
  readImpersonation,
} from '@/lib/impersonation-session';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { isPreviewFrame } from '@/lib/preview-frame';

/**
 * API base URL from environment
 * Default: Port 4000 (NestJS API)
 */
/**
 * Ensure VITE_API_URL includes /api/v1 suffix
 * Railway Web Service should set: VITE_API_URL=https://designdeckapi-production.up.railway.app/api/v1
 */
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;

  if (!envUrl) {
    return 'http://localhost:4000/api/v1';
  }

  // If URL doesn't end with /api/v1, append it
  if (!envUrl.endsWith('/api/v1')) {
    return `${envUrl.replace(/\/$/, '')}/api/v1`;
  }

  return envUrl;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Token storage keys
 */
const ACCESS_TOKEN_KEY = 'designdeck_access_token';
const REFRESH_TOKEN_KEY = 'designdeck_refresh_token';

/**
 * Token management utilities
 */
export const tokenStorage = {
  // An impersonation tab (lib/impersonation-session.ts) keeps its access
  // token in sessionStorage and has no refresh token. It must never read or
  // write the admin's tokens in localStorage, which every tab shares.
  //
  // The seller's Direct Sale preview frame (lib/preview-frame.ts) has no
  // tokens at all: it shares the origin with the seller's app, and a 401 in
  // there must never refresh, rotate or clear the seller's session.
  getAccessToken: () =>
    isPreviewFrame()
      ? null
      : isImpersonationTab()
        ? sessionStorage.getItem(IMPERSONATION_ACCESS_TOKEN_KEY)
        : localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () =>
    isPreviewFrame() || isImpersonationTab()
      ? null
      : localStorage.getItem(REFRESH_TOKEN_KEY),

  setTokens: (accessToken: string, refreshToken: string) => {
    if (isPreviewFrame()) return;
    if (isImpersonationTab()) {
      sessionStorage.setItem(IMPERSONATION_ACCESS_TOKEN_KEY, accessToken);
      return;
    }
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  clearTokens: () => {
    if (isPreviewFrame()) return;
    if (isImpersonationTab()) {
      sessionStorage.removeItem(IMPERSONATION_ACCESS_TOKEN_KEY);
      return;
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/**
 * Axios instance with interceptors
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - adds the auth token and the Shop Language.
 *
 * Every response that carries catalogue names is shaped by the language,
 * so it travels as `Accept-Language` on every request rather than as a
 * body field on the few endpoints that happened to need it.
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['Accept-Language'] = getIframeLanguage();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * What happens once a session is gone for good (no refresh token, or the
 * refresh itself failed). The default reloads into the login page; the auth
 * store swaps in a handler that turns the expiry into state when the app
 * runs embedded, where a login page has no meaning. The client stays free
 * of store imports: the store already imports `tokenStorage` from here.
 */
type UnauthorizedHandler = () => void;

const redirectToLogin: UnauthorizedHandler = () => {
  window.location.href = '/login';
};

let unauthorizedHandler: UnauthorizedHandler = redirectToLogin;

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

/**
 * Flag to prevent multiple refresh attempts
 */
let isRefreshing = false;

interface RefreshSubscriber {
  onToken: (token: string) => void;
  onError: (error: unknown) => void;
}

let refreshSubscribers: RefreshSubscriber[] = [];

/**
 * Subscribe to token refresh
 */
const subscribeTokenRefresh = (subscriber: RefreshSubscriber) => {
  refreshSubscribers.push(subscriber);
};

/**
 * Notify all subscribers with new token
 */
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((subscriber) => subscriber.onToken(token));
  refreshSubscribers = [];
};

/**
 * Reject every request parked on the refresh (nothing else would ever
 * settle them) and reopen the refresh gate.
 */
const rejectRefreshSubscribers = (error: unknown) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  isRefreshing = false;
  subscribers.forEach((subscriber) => subscriber.onError(error));
};

/**
 * The session is over: parked requests are rejected, the tokens are
 * cleared and the handler decides what the user sees.
 */
const onSessionLost = (error: unknown) => {
  rejectRefreshSubscribers(error);
  tokenStorage.clearTokens();
  unauthorizedHandler();
};

/**
 * The API throttles POST /auth/refresh per IP. A 429 says "not now", not
 * "this session is invalid", so it must never log the user out.
 */
const isThrottled = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error) && error.response?.status === 429;

/** Longest Retry-After the client waits out before retrying once. */
const REFRESH_RETRY_MAX_WAIT_MS = 5_000;
/** Wait used when the 429 carries no readable Retry-After. */
const REFRESH_RETRY_DEFAULT_WAIT_MS = 1_000;

/**
 * How long to wait before the single retry of a throttled refresh, or null
 * when the server asks for longer than is worth blocking the request on.
 */
const refreshRetryDelayMs = (error: AxiosError): number | null => {
  const raw: unknown = error.response?.headers?.['retry-after'];
  const retryAfterSeconds = Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(retryAfterSeconds)) {
    return REFRESH_RETRY_DEFAULT_WAIT_MS;
  }
  const delayMs = Math.max(0, retryAfterSeconds * 1000);
  return delayMs <= REFRESH_RETRY_MAX_WAIT_MS ? delayMs : null;
};

/**
 * POST /auth/refresh, retried once after a short throttle. A second 429 or
 * a long Retry-After is passed on to the caller unchanged.
 */
const requestTokenRefresh = async (refreshToken: string) => {
  const send = () =>
    axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  try {
    return await send();
  } catch (error) {
    if (!isThrottled(error)) throw error;
    const delayMs = refreshRetryDelayMs(error);
    if (delayMs === null) throw error;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return send();
  }
};

/**
 * Response interceptor - handles 401 and token refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // If no config or already retried, reject
    if (!originalRequest || (originalRequest as { _retry?: boolean })._retry) {
      return Promise.reject(error);
    }

    // An impersonation session has no refresh token and must not reach the
    // admin's session: a 401 ends it on the end screen (lib/impersonation-
    // session.ts), never a refresh, a localStorage wipe or /login.
    if (error.response?.status === 401 && isImpersonationTab()) {
      const marker = readImpersonation();
      const expired =
        marker !== null && Date.parse(marker.expiresAt) <= Date.now();
      endImpersonationSession(expired ? 'expired' : 'revoked');
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      const refreshToken = tokenStorage.getRefreshToken();

      // Direct Sale customers are anonymous by design; a rejected shop
      // signature must surface as an in-flow error, not tear the embedded
      // iframe out to the seller login page and destroy the design.
      const isDirectSale = window.location.pathname.startsWith('/direct-sale');

      // No refresh token, logout
      if (!refreshToken) {
        if (!isDirectSale) {
          onSessionLost(error);
        }
        return Promise.reject(error);
      }

      // Already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh({
            onToken: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(apiClient(originalRequest));
            },
            onError: reject,
          });
        });
      }

      // Start refreshing
      isRefreshing = true;
      (originalRequest as { _retry?: boolean })._retry = true;

      try {
        // Call refresh endpoint
        const response = await requestTokenRefresh(refreshToken);

        const { accessToken, refreshToken: newRefreshToken } =
          response.data.data;

        // Store new tokens
        tokenStorage.setTokens(accessToken, newRefreshToken);

        // Update header for current request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        // Notify subscribers
        onTokenRefreshed(accessToken);

        isRefreshing = false;

        // Retry original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        // Throttled: the refresh token is still valid, so keep the session
        // and fail only the requests waiting on this refresh. The next 401
        // tries again.
        if (isThrottled(refreshError)) {
          rejectRefreshSubscribers(refreshError);
          return Promise.reject(refreshError);
        }
        // Refresh failed, logout
        if (!isDirectSale) {
          onSessionLost(refreshError);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * API Response wrapper type
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

/**
 * Paginated response type
 */
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * API Error type
 */
export interface ApiError {
  statusCode: number;
  message: string | string[];
  error: string;
  timestamp: string;
  path: string;
}

/**
 * Extract error message from API error
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    if (apiError?.message) {
      return Array.isArray(apiError.message)
        ? (apiError.message[0] ?? error.message)
        : apiError.message;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

export default apiClient;
